import React, { useRef, useState } from 'react'
import {
  FaUpload as Upload,
  FaSpinner as Spinner,
  FaCheck as Check,
  FaTimes as X,
} from 'react-icons/fa'
import { useSampleUpload } from '@/hooks/useSampleUpload'

interface SampleUploadButtonProps {
  /** Fired when an upload completes successfully. The parent can
   *  immediately assign the returned sample to a pad. */
  onUploaded?: (sample: {
    id: string
    name: string
    s3Key: string
    playUrl: string
  }) => void
  /** Optional default name — defaults to the filename. */
  defaultName?: string
  /** "compact" fits inside the sequencer transport; "full" fits
   *  inside a dialog or side panel. */
  variant?: 'compact' | 'full'
}

/**
 * File picker + uploader for Plan 2b sample uploads. Wraps
 * `useSampleUpload` and shows inline status (loading / success /
 * error). Hidden `<input type=file>` is the native picker; the
 * visible button triggers it.
 */
export const SampleUploadButton: React.FC<SampleUploadButtonProps> = ({
  onUploaded,
  defaultName,
  variant = 'compact',
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const upload = useSampleUpload()
  const [status, setStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'success'; name: string }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' })

  const openPicker = () => {
    if (upload.isPending) return
    inputRef.current?.click()
  }

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset so re-uploading the same file re-fires change.
    e.target.value = ''
    if (!file) return

    setStatus({ kind: 'idle' })
    try {
      const { sample } = await upload.mutateAsync({
        file,
        name: defaultName ?? file.name,
      })
      setStatus({ kind: 'success', name: sample.name })
      onUploaded?.(sample)
      // Clear the success state after a couple of seconds so the
      // button resets.
      setTimeout(() => setStatus({ kind: 'idle' }), 2500)
    } catch (err) {
      setStatus({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Upload failed',
      })
    }
  }

  const baseBtn =
    variant === 'compact'
      ? 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-mono uppercase tracking-wider border transition-colors'
      : 'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors'

  const idleClass = upload.isPending
    ? 'opacity-60 cursor-wait border-brd-subtle text-content-tertiary'
    : 'border-brd-subtle text-content-secondary hover:border-brd-emphasis hover:text-content-primary'

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="audio/wav,audio/mpeg,audio/ogg,audio/x-wav,.wav,.mp3,.ogg"
        className="hidden"
        onChange={handleChange}
      />
      <button
        type="button"
        onClick={openPicker}
        disabled={upload.isPending}
        className={`${baseBtn} ${idleClass}`}
        title="Upload a custom sample"
      >
        {upload.isPending ? (
          <>
            <Spinner className="w-3 h-3 animate-spin" />
            <span>Uploading…</span>
          </>
        ) : status.kind === 'success' ? (
          <>
            <Check className="w-3 h-3 text-success" />
            <span className="truncate max-w-[140px]">{status.name}</span>
          </>
        ) : status.kind === 'error' ? (
          <>
            <X className="w-3 h-3 text-destructive" />
            <span className="truncate max-w-[140px]">{status.message}</span>
          </>
        ) : (
          <>
            <Upload className="w-3 h-3" />
            <span>Upload sample</span>
          </>
        )}
      </button>
    </>
  )
}
